import type { DateView } from '@mui/x-date-pickers';
import type { ContentNode } from '@newStore/documentUI/documentUITypes';
import type {
  AsideValidationRule,
  AsyncValidationRule,
  ValidationRule,
} from '@newStore/validation/validationTypes';
import type { ActionCreatorWithPayload, PayloadAction } from '@reduxjs/toolkit';
import type { Merge, RequireExactlyOne } from 'type-fest';
import type {
  AttachmentMetaInfo,
  Content,
  ContentHref,
  ContentRelation,
  ContentRelationType,
  ContentType,
  IsIncludedInRelation,
  IsPartOfRelation,
  LlinkidGoalType,
  WebConfigType,
  WebPage,
  WebSiteHref,
} from '../../types/apiTypes';
import type { RootState } from '../../types/rootStateTypes';

// We have a circular dependency problem as soon as we want to convert the config files to NodeTypeConfig
// thisFile -> index.ts  for values -> include a config file -> that configFile want to say he is of type NodeTypeConfig

// eslint-disable-next-line no-shadow
export enum NodeType {
  WEBPAGE2 = 'WEBPAGE2',
  PRO_THEME_HOME_FULL = 'PRO_THEME_HOME_FULL',
  PRO_THEME_HOME_TEXT = 'PRO_THEME_HOME_TEXT',
  PRO_THEME_HOME_PICTURE = 'PRO_THEME_HOME_PICTURE',
  PRO_TEXT_PAGE_TILE = 'PRO_TEXT_PAGE_TILE',
  PRO_TEXT_PAGE_STANDALONE = 'PRO_TEXT_PAGE_STANDALONE',
  PRO_DATABASE_TILE = 'PRO_DATABASE_TILE',
  PRO_DATABASE_STANDALONE = 'PRO_DATABASE_STANDALONE',
  PRO_DATABASE_ITEM = 'PRO_DATABASE_ITEM',
  PRO_BLOG_TILE = 'PRO_BLOG_TILE',
  PRO_BLOG_STANDALONE = 'PRO_BLOG_STANDALONE',
  PRO_BLOG_ITEM = 'PRO_BLOG_ITEM',
  PRO_DOWNLOAD_PAGE_TILE = 'PRO_DOWNLOAD_PAGE_TILE',
  PRO_DOWNLOAD_PAGE_STANDALONE = 'PRO_DOWNLOAD_PAGE_STANDALONE',
  PRO_FAQ_PAGE_TILE = 'PRO_FAQ_PAGE_TILE',
  PRO_CURRICULUM_PAGE_TILE = 'PRO_CURRICULUM_PAGE_TILE',
  PRO_TEMPORARY_PAGE_TILE = 'PRO_TEMPORARY_PAGE_TILE',
  PRO_TEMPORARY_PAGE_STANDALONE = 'PRO_TEMPORARY_PAGE_STANDALONE',
  PRO_GLOBAL_DATABASE = 'PRO_GLOBAL_DATABASE',
  PRO_GLOBAL_DATABASE_SECTION = 'PRO_GLOBAL_DATABASE_SECTION',
  PRO_STATIC_PAGE = 'PRO_STATIC_PAGE',
  PRO_THEME_SECTION = 'PRO_THEME_SECTION',
  // 'PRO Menu
  PRO_HOME_PAGE = 'PRO_HOME_PAGE',
  PRO_MENU_LEVEL_2 = 'PRO_MENU_LEVEL_2',
  PRO_MENU_LEVEL_3 = 'PRO_MENU_LEVEL_3',
  PRO_MENU_SECTION = 'PRO_MENU_SECTION',
  // Pro leerplansites so
  SHARED_MINI_DATABASE_ITEM = 'SHARED_MINI_DATABASE_ITEM',
  SHARED_FAQ = 'SHARED_FAQ',
  SHARED_ATTACHMENTS_GROUP = 'SHARED_ATTACHMENTS_GROUP',
  // Pro Newsletter
  TEASER = 'TEASER',
  PRONEWSITEM = 'PRONEWSITEM',
  PRONEWSLETTER = 'PRONEWSLETTER',
  PRONEWSLETTERTEMPLATE = 'PRONEWSLETTERTEMPLATE',
  PRO_NEWSLETTER_SECTION = 'PRO_NEWSLETTER_SECTION',
  PRO_NEWSLETTER_CALENDAR = 'PRO_NEWSLETTER_CALENDAR',
  PRO_NEWSLETTER_EVENT = 'PRO_NEWSLETTER_EVENT',
  // 'WWW
  WWW_STATIC_PAGE = 'WWW_STATIC_PAGE',
  WWW_HOME_PAGE = 'WWW_HOME_PAGE',
  WWW = 'WWW',
  WWWNEWSITEM = 'WWWNEWSITEM',
  JOB_OFFER = 'JOB_OFFER',
  PRESS = 'PRESS',
  // ZILL
  CURRICULUM_ZILL = 'CURRICULUM_ZILL',
  CURRICULUM_ZILL_CLUSTER = 'CURRICULUM_ZILL_CLUSTER',
  CURRICULUM_ZILL_DEVELOPMENT_FIELD = 'CURRICULUM_ZILL_DEVELOPMENT_FIELD',
  CURRICULUM_ZILL_DEVELOPMENT_THEME = 'CURRICULUM_ZILL_DEVELOPMENT_THEME',
  CURRICULUM_ZILL_GENERIC_GOAL = 'CURRICULUM_ZILL_GENERIC_GOAL',
  CURRICULUM_ZILL_CONTENT_GROUP = 'CURRICULUM_ZILL_CONTENT_GROUP',
  CURRICULUM_ZILL_DEVELOPMENT_CONTENT = 'CURRICULUM_ZILL_DEVELOPMENT_CONTENT',
  CURRICULUM_ZILL_LEERLIJN = 'CURRICULUM_ZILL_LEERLIJN',
  CURRICULUM_ZILL_LEERLIJN_AN = 'CURRICULUM_ZILL_LEERLIJN_AN',
  CURRICULUM_ZILL_LEERLIJN_PRE_REFERENCE = 'CURRICULUM_ZILL_LEERLIJN_PRE_REFERENCE',
  CURRICULUM_ZILL_LEERLIJN_CLUSTER = 'CURRICULUM_ZILL_LEERLIJN_CLUSTER',
  CURRICULUM_ZILL_LEERLIJN_POST_REFERENCE = 'CURRICULUM_ZILL_LEERLIJN_POST_REFERENCE',
  ZILL_ILLUSTRATION = 'ZILL_ILLUSTRATION',
  PRACTICAL_EXAMPLE = 'PRACTICAL_EXAMPLE',
  WEBPAGE = 'WEBPAGE',
  // KRC
  KRC_CURRICULUM = 'KRC_CURRICULUM',
  KRC_DISCIPLINE_CONTAINER = 'KRC_DISCIPLINE_CONTAINER',
  KRC_REFERENCE_FRAME = 'KRC_REFERENCE_FRAME',
  KRC_CURRICULUM_DISCIPLINE = 'KRC_CURRICULUM_DISCIPLINE',
  KRC_CURRICULUM_DOMAIN = 'KRC_CURRICULUM_DOMAIN',
  KRC_CURRICULUM_SUBDOMAIN = 'KRC_CURRICULUM_SUBDOMAIN',
  KRC_CURRICULUM_CLUSTER = 'KRC_CURRICULUM_CLUSTER',
  KRC_CURRICULUM_GOAL = 'KRC_CURRICULUM_GOAL',
  KRC_GOAL_SET = 'KRC_GOAL_SET',
  KRC_AGE_RANGE = 'KRC_AGE_RANGE',
  KRC_GOAL_SET_ITEM = 'KRC_GOAL_SET_ITEM',
  KRC_AGE_RANGE_ITEM = 'KRC_AGE_RANGE_ITEM',
  // ODET
  CURRICULUM_ODET = 'CURRICULUM_ODET',
  CURRICULUM_ODET_LEVEL = 'CURRICULUM_ODET_LEVEL',
  CURRICULUM_ODET_LEERGEBIED = 'CURRICULUM_ODET_LEERGEBIED',
  CURRICULUM_ODET_KEY_COMPETENCE = 'CURRICULUM_ODET_KEY_COMPETENCE',
  CURRICULUM_ODET_SUBSECTION = 'CURRICULUM_ODET_SUBSECTION',
  CURRICULUM_ODET_DEVELOPMENT_GOAL = 'CURRICULUM_ODET_DEVELOPMENT_GOAL',
  CURRICULUM_ODET_ENDTERM = 'CURRICULUM_ODET_ENDTERM',
  // 'LLINKID
  LLINKID_CURRICULUM = 'LLINKID_CURRICULUM',
  LLINKID_FOUNDATIONAL_CURRICULUM = 'LLINKID_FOUNDATIONAL_CURRICULUM',
  LLINKID_OKAN_CURRICULUM = 'LLINKID_OKAN_CURRICULUM',
  LLINKID_GOAL_LIST = 'LLINKID_GOAL_LIST',
  LLINKID_GOAL_SECTION = 'LLINKID_GOAL_SECTION',
  LLINKID_GOAL = 'LLINKID_GOAL',
  LLINKID_GOAL_DEMARCATION = 'LLINKID_GOAL_DEMARCATION',
  LLINKID_PEDAGOGICAL_TIP = 'LLINKID_PEDAGOGICAL_TIP',
  LLINKID_EXTRA_GOAL_INFORMATION = 'LLINKID_EXTRA_GOAL_INFORMATION',
  LLINKID_GOAL_EXPLANATION = 'LLINKID_GOAL_EXPLANATION',
  LLINKID_INITIAL_SITUATION = 'LLINKID_INITIAL_SITUATION',
  LLINKID_GUIDING_PRINCIPLE_GROUP = 'LLINKID_GUIDING_PRINCIPLE_GROUP',
  LLINKID_GUIDING_PRINCIPLE = 'LLINKID_GUIDING_PRINCIPLE',
  LLINKID_STATIC_PART = 'LLINKID_STATIC_PART',
  LLINKID_CONTEXT = 'LLINKID_CONTEXT',
  LLINKID_PEDAGOGICAL_DIDACTICAL_CONTEXT = 'LLINKID_PEDAGOGICAL_DIDACTICAL_CONTEXT',
  LLINKID_BASIC_MATERIAL_REQUIREMENTS = 'LLINKID_BASIC_MATERIAL_REQUIREMENTS',
  LLINKID_CORRELATION_ENDTERMS = 'LLINKID_CORRELATION_ENDTERMS',
  LLINKID_CONCRETIZATION = 'LLINKID_CONCRETIZATION',
  LLINKID_CONCORDANCE = 'LLINKID_CONCORDANCE',
  // Other generic root document: // Other generic root documents
  VISION_TEXT = 'VISION_TEXT',
  VISION_TEXT_SECTION = 'VISION_TEXT_SECTION',
  TERM = 'TERM',
  MARK_EXPLANATION = 'MARK_EXPLANATION',
  GLOBAL_DOCUMENT = 'GLOBAL_DOCUMENT',
  CONTACT_CARD = 'CONTACT_CARD',
  // "Themes": Refernece frames (ordeningskaders: // "Themes": Refernece frames (ordeningskaders)
  REFERENCE_FRAME = 'REFERENCE_FRAME',
  THEME = 'THEME',
  CURRICULUM_CLUSTER = 'CURRICULUM_CLUSTER',
  CURRICULUM_THEME = 'CURRICULUM_THEME',
  // BUILDING BLOCK
  CONTACT_GROUP = 'CONTACT_GROUP',
  PERSON_REFERENCE = 'PERSON_REFERENCE',
  REFERENCE_GROUP = 'REFERENCE_GROUP',
  REFERENCE = 'REFERENCE',
  SECTION = 'SECTION',
  PARAGRAPH = 'PARAGRAPH',
  IMAGE = 'IMAGE',
  IMAGE_GROUP = 'IMAGE_GROUP',
  VIDEO = 'VIDEO',
  VIDEO_GROUP = 'VIDEO_GROUP',
  EXAMPLE = 'EXAMPLE',
  ATTACHMENT = 'ATTACHMENT',
  ATTACHMENTS_GROUP = 'ATTACHMENTS_GROUP',
  DOWNLOAD = 'DOWNLOAD',
  MUSIC = 'MUSIC',
  QUOTE = 'QUOTE',
  FAQ = 'FAQ',
  FAQ_GROUP = 'FAQ_GROUP',
  SUMMARY = 'SUMMARY',
  LEGAL = 'LEGAL',
  WORD_IMPORT = 'WORD_IMPORT', // Not really a node type but only a building bloc
  // nodes only managable in asid: // nodes only managable in aside
  SOURCE = 'SOURCE',
  LINK_GROUP = 'LINK_GROUP',
}

// const nodeTypeArr = [NodeTypesEnum.ATTACHMENT, NodeTypesEnum.LLINKID_CURRICULUM];
// const contentTypeArr = [ContentTypes.ATTACHMENT, ContentTypes.LLINKID_CURRICULUM];
// const test = nodeTypeArr.some((type) => type === contentTypeArr[0]);
// console.log(test);

// export const NODETYPES = nodeTypes.reduce((acc, type) => {
//   acc[type] = type;
//   return acc;
// }, {} as any) as { [K in (typeof nodeTypes)[number]]: K & { __brand= 'NodeType' } };

// export type NodeType = (typeof NODETYPES)[keyof typeof NODETYPES];

export type WhiteBlacklistOption = {
  root?: { $$type?: string; foundational?: boolean; type?: ContentType };
  parent?: { $$type?: string; specificForAN?: boolean };
  self?: {
    hasNoChildren?: boolean; // used in the hack of WORD_IMPORT
  };
  webconfiguration?: {
    self?: Array<string>;
    parent?: Array<string>;
    inherited?: Array<string>;
  };
};

export type BuildingBlock = {
  type: NodeType;
  /** max amount of building blocks of this type allowed in the parent */
  max?: number;
  /** position index among siblings of the parent where this building block is allowed */
  position?: number;
  /** in which ancestor this building block is allowed */
  showInAncestorTypes?: NodeType[];
  /** in which ancestor this building block is not allowed */
  hideInAncestorTypes?: NodeType[];
  /** hide this building block from the ribbon (e.g. auto-created children) */
  hideInRibbon?: true;
  options?: any;
};

export type FilteredBuildingBlock = Omit<
  BuildingBlock,
  'showInAncestorTypes' | 'hideInAncestorTypes'
>;

export enum RequiredType {
  ERROR = 'ERROR',
  WARNING = 'WARNING',
}

export type EditComponentProperty =
  | keyof Content
  | keyof WebPage
  | `${keyof Content}.${string}`
  | `${keyof WebPage}.${string}`
  | `${keyof Content}[${number}]`;

export type AsideChangeMessageSelector<T> = (
  state: RootState,
  href: ContentHref,
  config: T,
) => string | null;

export type GenericEditComponent = {
  component: string; // the name of the component = the name of the content property unless specified
  property?: EditComponentProperty; // name of content property if != component name
  reactComponent: string; // displayName of the react component
  label: string; // specify label
  definiteArticle?: boolean; // in dutch will add 'het' instead of default 'de' before the label in sentences (fe error messages)
  width?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12; // width of the field (bootstrap 12-column)

  /** is the content property required */
  required?: RequiredType;
  /** define a custom message for when the field is empty */
  customRequiredMessage?: string;
  /** list of (new) validation selectors. */
  validations?: Array<AsideValidationRule>;
  /** selector that generates diff messages for the specific aside component */
};
//  & RequireExactlyOne<{
//   asideChangeMessageSelector: any;
//   noChangeMessage: boolean;
// }>;

export type AudienceTabComponentBase = Merge<
  GenericEditComponent,
  {
    /** in generic error message the message needed the singular version of the label which is an array and so plural */
    labelSingular: string;
    tag?: string; // not sure if we need this
    /** if value is undefined, it means it is taken over from parent (which might have got it from his parent and so on) */
    isInheritedProperty: true;
  }
>;

export type EditAsideAttachedContent = Merge<
  GenericEditComponent,
  {
    property: '$$html';
    reactComponent: 'AsideAttachedContent';
    options?: {
      showMarkerButton?: boolean;
      hideTermButton?: boolean;
    };
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideAttachedContent>; // changed
  }
>;

export type EditAsideTitle = Merge<
  GenericEditComponent,
  {
    component: 'title';
    property: 'title' | 'description';
    reactComponent: 'AsideTitle';
    options?: {
      showMarkerButton?: boolean;
      hideTermButton?: boolean;
    };
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideTitle>; // changed
  }
>;

export type EditAsideSubjects = Merge<
  GenericEditComponent,
  {
    component: 'subjects';
    property: 'subjects';
    reactComponent: 'AsideSubjects';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideSubjects>; // changed
  }
>;

export type EditAsideLeerlijnReference = Merge<
  GenericEditComponent,
  {
    reactComponent: 'AsideLeerlijnReference';
    component: 'leerlijnReference';
    property?: never;
    label: 'Verwijzing';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideLeerlijnReference>; // changed
  }
>;

export type ImageType = 'ILLUSTRATION' | 'THUMBNAIL' | 'COVER_IMAGE' | 'ICON' | 'BACKGROUND_IMAGE';
export type ImageLabel =
  | (string & {}) // allows autocomplete of the string below + any other string
  | 'Afbeelding'
  | 'Ikoon'
  | 'Omslagfoto'
  | 'Tegelfoto'
  | 'Afbeelding voor Social media'
  | 'Miniatuur'
  | 'Achtergrond';

export type ImageOptionField = 'alt';

export type EditAsideImage = Merge<
  GenericEditComponent,
  {
    component: 'image';
    property: 'attachments';
    reactComponent: 'AsideImage';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideImage>; // changed
    label: ImageLabel;
    options: {
      type: ImageType;
      crop?: {
        aspectRatio?: number;
        resize?: {
          width: number;
          height: number;
          quality?: number;
        };
      };
      fields?: Array<ImageOptionField>;
    };
  }
>;

export type EditAsideVideo = Merge<
  GenericEditComponent,
  {
    component: 'video';
    property: 'attachments';
    reactComponent: 'AsideVideo';
    label: 'Video';
    options: {
      type: ContentType.VIDEO;
    };
    required?: RequiredType.ERROR;
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideVideo>; // changed
  }
>;
export const isEditAsideVideo = (e: EditComponent): e is EditAsideVideo =>
  'reactComponent' in e && e.reactComponent === 'AsideVideo';

export type EditAsideFile = Merge<
  GenericEditComponent,
  {
    component: 'file';
    property: 'attachments';
    reactComponent: 'AsideFile';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideFile>; // changed
    options: {
      validExtensions: string;
      type: AttachmentMetaInfo['type'];
    };
  }
>;

export type EditAsideFoundationalSuggestions = Merge<
  GenericEditComponent,
  {
    component: 'llinkidFoundationalSuggestions';
    reactComponent: 'AsideFoundationalSuggestions';
    label: string;
    options: {
      relationType: string;
      listColumn: string;
    };
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideFoundationalSuggestions>; // changed
  }
>;

export type EditAsideNamedSet = Merge<
  AudienceTabComponentBase,
  {
    component: 'namedSet';
    property: 'mainstructuresOuTypeCombinations' | 'positions';
    tag: 'mainstructure_outype_ext_comm' | 'doelgroepen';
    reactComponent: 'AsideNamedSet';
    subLabel: string;
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideNamedSet>; // changed
  }
>;

export type EditAsideCoverage = Merge<
  AudienceTabComponentBase,
  {
    component: 'coverage';
    reactComponent: 'AsideCoverage';
    property: 'coverage';
    label: "Regio's";
    labelSingular: 'Regio';
    isInheritedProperty: true;
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideCoverage>; // changed
  }
>;

export type EditAsideAccessRights = Merge<
  AudienceTabComponentBase,
  {
    component: 'accessRights';
    reactComponent: 'AsideAccessRights';
    property: 'descendantsAccessRights' | 'accessRights';
    isInheritedProperty: true;
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideAccessRights>; // changed
  }
>;

export const CurriculumTypeKeys = {
  REGULAR: 'REGULAR',
  FOUNDATIONAL: 'FOUNDATIONAL',
  OKAN: 'OKAN',
};
export type DropDownOptionsType = {
  key: string;
  label: string;
};

export type EditAsideCurriculumType = Merge<
  GenericEditComponent,
  {
    component: 'curriculumType';
    reactComponent: 'AsideCurriculumType';
    property?: never;
    options: Array<DropDownOptionsType>;
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideCurriculumType>; // changed
  }
>;

export type EditAsideDescription = Merge<
  GenericEditComponent,
  {
    component: 'description';
    property: 'description' | 'shortdescription';
    fullLabel?: string;
    reactComponent: 'AsideDescription';
    maxLength?: number;
    options?: {
      showMarkerButton?: boolean;
      hideTermButton?: boolean;
      height?: number;
      toolbar?: string;
    };
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideDescription>; // changed
  }
>;

export type EditAsideContactsOrCreators = Merge<
  GenericEditComponent,
  {
    component: 'contacts' | 'authors';
    property: 'contacts' | 'creators';
    reactComponent: 'AsidePersonPicker';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideContactsOrCreators>; // changed
    options: {
      queryParams: any;
      multiple?: boolean;
    };
  }
>;

export type EditAsideFreeTextAuthors = Merge<
  GenericEditComponent,
  {
    component: 'authors';
    property: 'creators[0]';
    reactComponent: 'AsideFreeTextAuthor';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideFreeTextAuthors>; // changed
  }
>;

export type EditAsideTeaserImportance = Merge<
  GenericEditComponent,
  {
    component: 'teaserImportance';
    property: 'importance';
    reactComponent: 'AsideTeaserImportance';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideTeaserImportance>; // changed
  }
>;

export type EditAsideCheckbox = Merge<
  GenericEditComponent,
  {
    component: string;
    property: EditComponentProperty;
    reactComponent: 'AsideCheckbox';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideCheckbox>; // changed
  }
>;

export type EditAsideStaticHtml = Merge<
  GenericEditComponent,
  {
    component: 'staticHtml';
    property: '$$html';
    reactComponent: 'AsideStaticHtml';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideStaticHtml>; // changed
  }
>;

export type EditAsideIdentifier = Merge<
  GenericEditComponent,
  {
    component: 'identifier';
    property: 'identifiers';
    reactComponent: 'AsideIdentifier';
    readonly?: boolean;
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideIdentifier>; // changed
  }
>;

export type EditAsidePreviousVersion = Merge<
  GenericEditComponent,
  {
    component: 'previousVersion';
    reactComponent: 'AsidePreviousVersion';
    readonly: true;
    noChangeMessage: true;
  }
>;

export type EditAsideValidityPeriod = Merge<
  GenericEditComponent,
  {
    component: 'validityPeriod';
    reactComponent: 'AsideValidityPeriod';
    readonly: true;
    noChangeMessage: true;
  }
>;

export type EditAsideNewsletterSender = Merge<
  GenericEditComponent,
  {
    component: 'newsletterSender';
    property: 'newsletterSender.href';
    reactComponent: 'AsideNewsletterSender';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideNewsletterSender>; // changed
  }
>;

export type EditAsideSources = Merge<
  GenericEditComponent,
  {
    component: 'sources';
    property?: never;
    reactComponent: 'AsideSources';
    hiddenChildren: {
      containerType: ContentType.STRUCTURED_DOCUMENT;
      items: [
        {
          type: ContentType.SOURCE;
          label: string;
        },
      ];
    };
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideSources>; // changed
  }
>;

export type EditAsideVersion = Merge<
  GenericEditComponent,
  {
    component: 'version';
    reactComponent: 'AsideVersion';
    readonly: true;
    noChangeMessage: true;
  }
>;

export type EditAsidePreviousVersionItems = Merge<
  GenericEditComponent,
  {
    component: 'previousVersionItems';
    reactComponent: 'AsidePreviousVersionItems';
    property?: never;
    options: {
      relationTypes: ['REPLACES']; // ContentRelation['relationtype'];
      modal: 'llinkidPreviousVersionModal' | 'llinkidGoalRelationsModal';
      modalTitle: string;
      listColumn?: 'description';
      placeholder: string;
      relation: string;
      errorMessage: string;
      type:
        | ContentType.LLINKID_GOAL
        | ContentType.LLINKID_PEDAGOGICAL_TIP
        | ContentType.LLINKID_EXTRA_GOAL_INFORMATION
        | ContentType.LLINKID_GOAL_EXPLANATION
        | ContentType.LLINKID_INITIAL_SITUATION;
    };
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsidePreviousVersionItems>;
  }
>;

export type EditAsideLlinkidGoalType = Merge<
  GenericEditComponent,
  {
    component: 'llinkidGoalType';
    property: 'llinkidGoalType';
    reactComponent: 'AsideSimpleDropDown';
    options: {
      clearable: boolean;
      options: Array<{ key: LlinkidGoalType; label: string }>;
    };
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideLlinkidGoalType>; // changed
  }
>;

export type EditAsideImportance = Merge<
  GenericEditComponent,
  {
    component: 'importance';
    property: 'importance';
    reactComponent: 'AsideSimpleDropDown';
    options: {
      clearable: boolean;
      options: Array<{ key: string; label: string }>;
    };
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideImportance>; // changed
  }
>;

export type EditAsideRealisationPeriod = Merge<
  GenericEditComponent,
  {
    component: 'realisationPeriod';
    property: 'realisationPeriod';
    reactComponent: 'AsideSimpleDropDown';
    options: {
      clearable: boolean;
      options: Array<{ key: number; label: string }>;
    };
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideRealisationPeriod>; // changed
  }
>;

export type EditAsideNewsletterType = Merge<
  GenericEditComponent,
  {
    component: 'newsletterType';
    property: 'newsletterType.href';
    reactComponent: 'AsideSimpleDropDown';
    readonly?: boolean;
    options: {
      clearable: boolean;
      options: Array<{ key: string; label: string }>;
    };
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideNewsletterType>; // changed
  }
>;

export type EditAsideSimpleDropDown =
  | EditAsideNewsletterType
  | EditAsideLlinkidGoalType
  | EditAsideImportance
  | EditAsideRealisationPeriod;

export const isEditAsideSimpleDropDown = (e: EditComponent): e is EditAsideSimpleDropDown =>
  'reactComponent' in e && e.reactComponent === 'AsideSimpleDropDown';

export const isEditAsideImage = (e: EditComponent): e is EditAsideImage =>
  'reactComponent' in e && e.reactComponent === 'AsideImage';

export type EditAsideSecondaryEducationType = Merge<
  GenericEditComponent,
  {
    component: 'secondaryEducationTypes';
    property: 'secondaryEducationTypes[0]';
    reactComponent: 'AsideSecondaryEducationType';
    options: {
      clearable: boolean;
    };
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideSecondaryEducationType>; // changed
  }
>;

export type EditAsideMinimumGoal = Merge<
  GenericEditComponent,
  {
    component: 'minimumGoals';
    property?: never;
    reactComponent: 'AsideMinimumGoal';
    options: {
      clearable: boolean;
      relationTypes: Array<ContentRelationType>;
    };
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideMinimumGoal>; // changed
  }
>;

export type EditAsideGoalRelations = Merge<
  GenericEditComponent,
  {
    component: 'goalImplements' | 'goalOdetEndTermReferences' | 'goalRelations';
    reactComponent: 'AsideGoalRelations';
    property?: never;
    options: {
      relationTypes: Array<ContentRelationType>;
      modal?: 'llinkidGoalRelationsModal' | 'llinkidGoalOdetEndTermReferencesModal';
      listColumn: 'description' | 'title';
      modalTitle?: string;
      revertedRelationDirection?: boolean;
      toType: ContentType.LLINKID_GOAL | ContentType.CURRICULUM_ODET_DEVELOPMENT_GOAL;
    };
    readonly?: boolean;
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideGoalRelations>;
  }
>;

type DatePickerOptions = {
  openTo: DateView;
  shouldDisableDate?: (day: Date) => boolean;
};

export type EditAsideExpiryDatePicker = Merge<
  GenericEditComponent,
  {
    component: 'expiryDate' | 'newUntilDate';
    property: 'expiryDate' | 'newUntilDate';
    reactComponent: 'AsideDatePicker';
    options: DatePickerOptions;
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideExpiryDatePicker>; // changed
  }
>;

export type EditAsideIssuedDatePicker = Merge<
  GenericEditComponent,
  {
    component: 'issued';
    property: 'issued';
    reactComponent: 'AsideIssuedDatePicker';
    options: DatePickerOptions;
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideIssuedDatePicker>; // changed
  }
>;

export type EditAsideDateToSendDatePicker = Merge<
  GenericEditComponent,
  {
    component: 'dateToSend';
    property?: never;
    reactComponent: 'AsideDateToSendPicker';
    options: DatePickerOptions;
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideDateToSendDatePicker>; // changed
  }
>;

export type EditAsideReferenceFrameReferences = Merge<
  GenericEditComponent,
  {
    component: 'educationalComponents' | 'educationalPointers';
    reactComponent: 'AsideReferenceFrameReferences';
    property?: never;
    options: {
      relationTypes: Array<ContentRelationType>;
      listColumn: 'title';
      revertedRelationDirection: boolean;
      referenceFrame: ContentHref;
    };
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideReferenceFrameReferences>; // changed
  }
>;

export type EditAsideColor = Merge<
  GenericEditComponent,
  {
    component: 'color';
    property: 'color';
    reactComponent: 'AsideColor';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideColor>; // changed
  }
>;

export type EditAsideLinks = Merge<
  GenericEditComponent,
  {
    component: 'linkGroup';
    reactComponent: 'AsideLinks';
    options?: {
      type: 'group';
      modalTitle?: string;
    };
    hiddenChildren?: {
      containerType: ContentType.LINK_GROUP;
      items: Array<{
        type: ContentType.REFERENCE;
        label: string;
      }>;
    };
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideLinks>; // changed
  }
>;

export type EditAsideCurriculumTheme = Merge<
  GenericEditComponent,
  {
    component: 'curriculumTheme';
    reactComponent: 'AsideCurriculumTheme';
    property: 'themes';
    subLabel: string;
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideCurriculumTheme>; // changed
  }
>;

export type EditAsideCurriculumSelector = Merge<
  GenericEditComponent,
  {
    component: 'curriculumSelector';
    reactComponent: 'AsideCurriculumSelector';
    property: 'themes';
    noChangeMessage: true;
  }
>;

export type EditAsideTeaserLinkedContent = Merge<
  GenericEditComponent,
  {
    component: 'teaserLinkedContent';
    reactComponent: 'AsideTeaserLinkedContent';
    hiddenChildren: {
      containerType: ContentType.TEASER;
      items: [
        {
          type: ContentType.REFERENCE;
          label: 'link';
        },
      ];
    };
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideTeaserLinkedContent>; // changed
  }
>;

export type EditAsideConcordanties = Merge<
  GenericEditComponent,
  {
    component: 'concordanties';
    reactComponent: 'AsideConcordanties';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideConcordanties>; // changed
  }
>;

export type EditAsideDevelopmentPhase = Merge<
  GenericEditComponent,
  {
    component: 'developmentPhase';
    reactComponent: 'AsideDevelopmentPhase';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideDevelopmentPhase>; // changed
  }
>;

export type EditAsideWebFacetsPlaceholder = Merge<
  GenericEditComponent,
  {
    component: 'webFacetsPlaceHolder';
    reactComponent: 'AsideWebFacetPlaceHolder';
    noChangeMessage: true;
  }
>;

export type EditAsideAgeRange = Merge<
  GenericEditComponent,
  {
    component: 'ageRange';
    reactComponent: 'AsideAgeRange';
    property?: never;
    properties: ['startage', 'endage'];
    options?: {
      editMonth: boolean;
    };
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideAgeRange>;
  }
>;

export type EditAsideZillGoals = Merge<
  GenericEditComponent,
  {
    component: 'zillGoals';
    reactComponent: 'AsideZillGoals';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideZillGoals>;
  }
>;

export type KrcGoalRelationUiType =
  | 'PRECEDES_BEFORE'
  | 'PRECEDES_AFTER'
  | 'IS_HANDLED_TOGETHER'
  | 'IS_MANDATORY_TOGETHER'
  | 'IS_ALTERNATIVE_FOR';

export type EditAsideKrcGoalRelations = Merge<
  GenericEditComponent,
  {
    component:
      | 'krcGoalRelationsBefore'
      | 'krcGoalRelationsAfter'
      | 'krcGoalRelationsHandledTogether'
      | 'krcGoalRelationsMandatoryTogether'
      | 'krcGoalRelationsAlternative';
    reactComponent: 'AsideKrcGoalRelations';
    options: {
      relationType: KrcGoalRelationUiType;
    };
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideKrcGoalRelations>;
  }
>;

export type EditAsideAttachmentGroupType = Merge<
  GenericEditComponent,
  {
    component: 'attachmentGroupType';
    reactComponent: 'AsideAttachmentGroupType';
    options: Array<DropDownOptionsType>;
    subLabel: string;
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideAttachmentGroupType>; // changed
  }
>;

export type EditAsideReferenceFramePicker = Merge<
  GenericEditComponent,
  {
    component: 'referenceFramePicker';
    reactComponent: 'AsideReferenceFramePicker';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideReferenceFramePicker>; // changed
    options: {
      referenceFrameHref: ContentHref;
      types: ContentType[];
    };
  }
>;

export type EditAsideStudyProgrammes = Merge<
  GenericEditComponent,
  {
    component: 'studyProgrammes';
    reactComponent: 'AsideStudyProgrammes';
    property: 'applicability.studyProgrammes';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideStudyProgrammes>; // changed
  }
>;

export type EditAsidePracticalExampleReferences = Merge<
  GenericEditComponent,
  {
    component: 'practicalExampleReferences';
    reactComponent: 'AsidePracticalExampleReferences';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsidePracticalExampleReferences>; // changed
  }
>;

export type EditAsideZillIllustrations = Merge<
  GenericEditComponent,
  {
    component: 'zillIllustrations';
    reactComponent: 'AsideZillIllustrations';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideZillIllustrations>; // changed
  }
>;

export type EditAsideGoalsByZillIllustration = Merge<
  GenericEditComponent,
  {
    component: 'goalsByZillIllustration';
    reactComponent: 'AsideGoalsByZillIllustration';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideGoalsByZillIllustration>; // changed
  }
>;

export type EditAsideMenuItemReference = Merge<
  GenericEditComponent,
  {
    component: 'menuItemReference';
    reactComponent: 'AsideMenuItemReference';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideMenuItemReference>; // changed
    options?: {
      externalLinkHidden?: boolean;
    };
  }
>;

export type EditAsideReferenceGroup = Merge<
  GenericEditComponent,
  {
    component: 'referenceGroup';
    reactComponent: 'AsideReferenceGroup';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideReferenceGroup>; // changed
    hiddenChildren: {
      containerType: ContentType.REFERENCE_GROUP;
      items: [
        {
          type: ContentType.REFERENCE;
          label: 'verwijzing';
        },
      ];
    };
  }
>;

export const isEditAsideStudyProgrammes = (e: EditComponent): e is EditAsideStudyProgrammes =>
  'reactComponent' in e && e.reactComponent === 'AsideStudyProgrammes';

export type EditAsideThemes = Merge<
  GenericEditComponent,
  {
    component: 'themes' | 'databaseTypes' | 'faqType';
    reactComponent: 'AsideThemes';
    property: 'themes';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideThemes>;
    options: {
      referenceFrameHref: ContentHref;
      types: ContentType[];
      highlightOldCurrItems?: boolean;
    };
  }
>;

export const isEditAsideThemes = (e: EditComponent): e is EditAsideThemes =>
  'reactComponent' in e && e.reactComponent === 'AsideThemes';

export type EditAsideWebPath = Merge<
  GenericEditComponent,
  {
    component: 'webPath';
    webPage: true;
    reactComponent: 'AsideWebSimpleInput';
    property: 'path';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideWebPath>;
  }
>;

export type EditAsideWebType = Merge<
  GenericEditComponent,
  {
    component: 'webType';
    webPage: true;
    reactComponent: 'AsideWebType';
    property: 'template.href';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideWebType>;
  }
>;

export type EditAsideWebFacets = Merge<
  GenericEditComponent,
  {
    component: 'webFacets';
    webPage: true;
    reactComponent: 'AsideWebFacets';
    property: 'options.facets';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideWebFacets>;
  }
>;

export type EditAsideWebBannerHighlight = Merge<
  GenericEditComponent,
  {
    component: 'webBannerHighlight';
    webPage: true;
    reactComponent: 'AsideWebSimpleCheckbox';
    property: 'options.banner.highlight';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideWebBannerHighlight>;
  }
>;

export type EditAsideWebBannerTitle = Merge<
  GenericEditComponent,
  {
    component: 'webBannerTitle';
    webPage: true;
    reactComponent: 'AsideWebSimpleInput';
    property: 'options.banner.title';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideWebBannerTitle>;
  }
>;

export type EditAsideWebBannerSubtitle = Merge<
  GenericEditComponent,
  {
    component: 'webBannerSubtitle';
    webPage: true;
    reactComponent: 'AsideWebSimpleInput';
    property: 'options.banner.subtitle';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideWebBannerSubtitle>;
  }
>;

export type EditAsideWebBannerCallToAction = Merge<
  GenericEditComponent,
  {
    component: 'webBannerCallToAction';
    webPage: true;
    reactComponent: 'AsideWebSimpleInput';
    property: 'options.banner.cta';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideWebBannerCallToAction>;
  }
>;

export type EditAsideWebBannerUrl = Merge<
  GenericEditComponent,
  {
    component: 'webBannerUrl';
    webPage: true;
    reactComponent: 'AsideWebSimpleInput';
    property: 'options.banner.url';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideWebBannerUrl>;
  }
>;

export type EditAsideWebOldUrls = Merge<
  GenericEditComponent,
  {
    component: 'webOldUrls';
    webPage: true;
    reactComponent: 'AsideWebOldUrls';
    property: 'oldLocations';
    asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideWebOldUrls>;
  }
>;

export type EditAsideWebBannerFromOrTo =
  | Merge<
      GenericEditComponent,
      {
        component: 'webBannerFrom';
        webPage: true;
        reactComponent: 'AsideWebDatePicker';
        property: 'options.banner.from';
        asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideWebBannerFromOrTo>;
      }
    >
  | Merge<
      GenericEditComponent,
      {
        component: 'webBannerTo';
        webPage: true;
        reactComponent: 'AsideWebDatePicker';
        property: 'options.banner.to';
        asideChangeMessageSelector: AsideChangeMessageSelector<EditAsideWebBannerFromOrTo>;
      }
    >;

export type EditComponent =
  | EditAsideWebPath
  | EditAsideWebType
  | EditAsideWebFacets
  | EditAsideWebBannerHighlight
  | EditAsideWebBannerTitle
  | EditAsideWebBannerSubtitle
  | EditAsideWebBannerCallToAction
  | EditAsideWebOldUrls
  | EditAsideWebBannerUrl
  | EditAsideWebBannerFromOrTo
  | EditAsideStudyProgrammes
  | EditAsideSources
  | EditAsideSimpleDropDown
  | EditAsideExpiryDatePicker
  | EditAsideDateToSendDatePicker
  | EditAsideIssuedDatePicker
  | EditAsideIdentifier
  | EditAsidePreviousVersion
  | EditAsideStaticHtml
  | EditAsideTitle
  | EditAsideSubjects
  | EditAsideLeerlijnReference
  | EditAsideSecondaryEducationType
  | EditAsideNewsletterSender
  | EditAsideDescription
  | EditAsideValidityPeriod
  | EditAsideVersion
  | EditAsidePreviousVersionItems
  | EditAsideGoalRelations
  | EditAsideTeaserImportance
  | EditAsideReferenceFrameReferences
  | EditAsideCheckbox
  | EditAsideFreeTextAuthors
  | EditAsideAttachedContent
  | EditAsideContactsOrCreators
  | EditAsideImage
  | EditAsideVideo
  | EditAsideColor
  | EditAsideLinks
  | EditAsideCurriculumTheme
  | EditAsideCurriculumSelector
  | EditAsideTeaserLinkedContent
  | EditAsideConcordanties
  | EditAsideDevelopmentPhase
  | EditAsideWebFacetsPlaceholder
  | EditAsideAgeRange
  | EditAsideZillGoals
  | EditAsideKrcGoalRelations
  | EditAsideReferenceFramePicker
  | EditAsideThemes
  | EditAsideNamedSet
  | EditAsideFoundationalSuggestions
  | EditAsideCoverage
  | EditAsidePracticalExampleReferences
  | EditAsideAccessRights
  | EditAsideCurriculumType
  | EditAsideZillIllustrations
  | EditAsideAttachmentGroupType
  | EditAsideFile
  | EditAsideGoalsByZillIllustration
  | EditAsideMenuItemReference
  | EditAsideReferenceGroup
  | EditAsideMinimumGoal;

// component that does not edit a Content property, but edits hidden content items that are part of this node
export type EditHiddenChildrenComponent = Extract<EditComponent, { hiddenChildren: any }>;

export type EditAttachmentsComponent = Extract<EditComponent, { property: 'attachments' }>;

export type WebTabComponent = Extract<EditComponent, { webPage: true }>;

export type AudienceTabComponent = EditAsideNamedSet | EditAsideCoverage | EditAsideAccessRights;

type WithAsideChangeMessageSelector<T> = T extends { asideChangeMessageSelector: any } ? T : never;

export type EditComponentWithAsideChangeMessageSelector =
  WithAsideChangeMessageSelector<EditComponent>;

export type FilterableEditComponent = EditComponent & {
  /** component will only appear if the node has any of the given NodeTypes as an ancestor */
  showInAncestorTypes?: NodeType[];
  /** component will not appear if the node has any of the given NodeTypes as an ancestor */
  hideInAncestorTypes?: NodeType[];
};

export type RootEditComponent = EditComponent & {
  // a rootnode cannot have these properties.
  showInAncestorTypes?: never;
  hideInAncestorTypes?: never;
};

export const isWebTabComponent = (e: EditComponent | WebTabComponent): e is WebTabComponent =>
  'webPage' in e && e.webPage;

export const isEditAttachmentsComponent = (e: EditComponent): e is EditAttachmentsComponent =>
  'property' in e && e.property === 'attachments';

export const isHiddenChildrenComponent = (e: EditComponent): e is EditHiddenChildrenComponent =>
  'hiddenChildren' in e;

export type FilteredEditComponent = RootEditComponent;
//  Omit<
//   FilterableEditComponent,
//   'showInAncestorTypes' | 'hideInAncestorTypes'
// >;

export type InlineField = 'title' | 'description' | 'html';

export type RootNodeCategory = 'PRO' | 'ZILL' | 'WWW' | 'LEERPLANSITES_SO' | 'LEERPLAN' | 'GENERAL';

type GenericNode = {
  information: {
    /** used in several places like error messages to add 'de' (false) or 'het' (true) before the label */
    definiteArticle?: boolean;
    /** the label used in the document, error messages and proposal diffText */
    single: string;
    /** the plural version of the label for some cases. */
    plural: string;
    /** not used in code, but useful to add some information about the NodeType */
    description?: string;
    /** used in the ribbon */
    icon?: any;
    ribonIcon?: any;
    ribbonTitle?: string;
    /** used in List screen to group items (only relevant in RootNodeConfig) */
    category?: RootNodeCategory; // TODO: make it only for root node types.
  };
  /** properties that also exist on Content. Used for
   * 1. Matching with content item do determine the node type
   * 2. when creating a node type adding these default properties to the created Content item.
   */
  // node: {
  //   // 1st parameter for NodeType match (in future (if all NodeTypeConfigs are TypeScript and less branches) => nodeTypeMatch: { content, webTemplateHref, rootType})
  //   type?: ContentType;
  //   tags?: ContentTag[];
  //   specificForAN?: boolean;
  //   importance?: 'LOW' | 'MEDIUM' | 'HIGH';
  //   foundational?: boolean;
  // } | null; // null: you never want to match, it has some other purpose (temporary SECTION_NEW, but alos WORD_IMPORT if that needs to stay)
  /**
   * any content child of this node will not be visible in the document (and should be managed from the aside as part of this node)
   * this also means that error messages and proposal messages for the content children are also related to this node
   */
  hideChildrenInDocument?: true;
  /** a node can be deprecated, but still there to show in old documents (for example Odet leergebied in Odet 2014). So it is valid in the document, but will no longer be selectable in the ribbon. */
  isDeprecated?: true;
};

/**
 * Configuration for Content items that can only occur as hidden items of another node and managed from the aside, so they are not a Node
 * therefore these kind of configs will not have a lot of properties since the only thing you need to detect is that they are not a node and they are hidden
 */
export type HiddenContentItemConfig = GenericNode & {
  hideInDocument: true;
};

/**
 * As opposed to HiddenContenItemConfig, Configuration for Content Items that become visible as a node in the Document
 * So this is the configuration for any kind of node in the document, a root or a "building block" below it.
 */
type DocumentNodeConfig = GenericNode & {
  /** content properties that are added when creating the node, but on which we don't want to match in the node definition */
  createDefaults?: any; // Partial<Content> <- see PARAGRAPH, I can not make Typescript happy

  nodeSelector?: (state: RootState, href: ContentHref) => ContentNode;
  deleteFromListValidations?: Array<(key: string) => Promise<any>>;
  deleteValidations?: Array<(node: any, state?: any) => any>;
  createValidations?: Array<{ cmd: any; failAction: any }>;
  validationRules?: Array<ValidationRule | AsyncValidationRule>;

  /**
   * used for LLINKID, to create some children by default
   */
  createChildDefaults?: Array<{
    type: ContentType;
    readorder: number;
  }>;
  buildingBlocks: Array<BuildingBlock>;
  edit: Array<FilterableEditComponent>;
  webTab?: Array<WebTabComponent>;
  /**
   * on the audience tab you also get an accessRights component.
   * This is not automatically the same as the root like the "real audiencecomponents", but it only needs to be there for a limited set of nodeTypes (fe. attachments_group).
   * In most cases just a boolean to have the default accessRightsComponent, but can also be an Object overwriting some properties of the default accessRightsComponent
   */
  addAccessRightsToAudienceTab?: boolean | AudienceTabComponent;
  /** you are not allowed to delete this node by selecting it in Document View */
  isNotDeletable?: true;

  // not sure for the following properties for which kind of nodes (root or building block or both) this is
  createModal?: any;
  isImportable?: boolean;
  documentViewIdentifier?: string; // what is this?
  globalAttachmentsGroupContainer?: boolean; // used in documentHelpers.js method which is refered by asideAttachmentsComponent but you can just look at the edit.some(c => c.component === 'attachments' && c.options?.global === true)
};

/**
 * Configuration for nodes that are the root of a document
 * Need also a lot of extra properties that are necessary for:
 *  - the list screen
 *  - properties that are applicable for the whole document
 *  - properties that are necessary for the initialisation of the document
 */
export type RootNodeConfig = DocumentNodeConfig & {
  /** Is this node searchable as a document in the List screen (if user has access to this nodetype) */
  isSearchable: boolean;
  /** Will be in the list of documents to create in the list screen (if user has access to this nodetype) */
  isCreatable?: boolean;
  /** if true edits on published documents will happen with proposals */
  allowSuggestions: boolean;
  /** node types that will be visualised in the table of contents */
  tocTypes: Array<NodeType>;

  edit: Array<RootEditComponent>;

  /**
   * when fetching the document these actions spicific for this NodeType will be dispatched.
   * This property is only on the non extended root type, because this happens before the /web/pages are loaded so they are solely based on the content item (generic NodeType)
   */
  onLoadActions?: Array<PayloadAction<any>>; // introduced in the refactored code
  preloadActions?: Array<any>; // old Redactie but still called in documentApiSaga => TODO! merge
  /** by default IS_INCLUDED_IN and IS_VERSION_OF. Prevent fetching of items linked to these relations (because like in Zill you do this in his own saga because the data model is more complex there) */
  relationsToIgnore?: ContentRelation['relationtype'][];
  /**
   * this root node AND all other child nodes in the document will also have the audience tab in the aside with the same components specified by the root
   * Audience properties (coverage, mainstructureAndOuTypes, positions) are inherited by nodes if they don't specify them themselves
   * Only exception are included Teasers which can define their own audience tab (because they are also a RootNodeType) that could in theory have different (more) components as the Newsletter (at this moment they are the same components)
   */
  audienceTab?: Array<AudienceTabComponent>;
  /**
   * if this node type will always have the same Web Template, on creation it will always get automatically a Web Page with these properties
   * When refactoring aside we can move this more generically to an onCreateAction
   */
  webconfiguration?: Partial<WebPage & { type?: string }>;
  websiteEdition?: Array<{ domain: string }>;
  /**
   * replacement of websiteEdition.
   * defines for which website this rootNode can have a webconfig.
   * if set to null, the rootNode will not have any webconfig.
   */
  websiteHref: WebSiteHref | null;
  ribbon?: {
    customCondition?: {
      type: string;
      childrenCount: number;
    }[];
    importFromWord?: boolean;
  };
  /** If set to true, you will not be able to delete any node in the document if the document is published */
  disableDeleteWhenIssued?: boolean;
  customEditorOptions?: {
    // in PRONEWSLETTER already defined on root as it should, but often defined in edit where it will be removed
    showMarkerButton?: boolean;
    hideTermButton?: boolean;
  };
  isCloneable?: boolean;
  previewModes?: Array<{ type: string; name: string; location: string }>;
  /** in this document you can only add these types in a Reference Group */
  referenceGroupTypes?: any; // TODO replace any to the real options from keys of constants.referenceGroupTypes
  onEditIncludedNode?: (contentNode: ContentHref) => void; // do something instead of opening the aside, used for GLOBAL_DOCUMENTS
};

/**
 * Configuration for Root nodes that are a more specific instance of some other generic root node
 * for example all the Pro Theme configurations that are a specific instance of WEBPAGE2
 * Given the webConfiguration they have they become a more specific instance of WEBPAGE2 with other configuration properties
 */
export type ExtendedRootNodeConfig = DocumentNodeConfig & {
  /** means this is a more specific instance of the given generic NodeType */
  extends: NodeType;
  /** 2nd parameter for NodeType match */
  // webTemplateHref?: WebTemplateHref;
  tocTypes: Array<NodeType>;
  audienceTab?: Array<AudienceTabComponent>;
  referenceGroupTypes?: any; // TODO replace any to the real options from keys of constants.referenceGroupTypes
};

// Configuration for Building Block nodes (non-root-nodes)
// they have all kind of properties related to editing the nodes in the document (creating a new one, replacing, deleting, collapsing)
export type BuildingBlockNodeConfig = DocumentNodeConfig & {
  /** if true, you will be able to collapse the node */
  isCollapsible: boolean;
  /** when opening this document this node will be collapsed (if the document has more nodes than settings.defaultCollapseNodeCountTreshold [usually set to 100]) */
  isCollapsedByDefault?: boolean;
  /** after selecting and deleting a node: show a pop-up where user needs to confirm if he is sure he wants to delete the node (delete in list screen always asks confirmation) */
  confirmDelete?: true;
  /** conditionaly show a pop-up where user needs to confirm if he is sure he wants to delete the node with custom message */
  customConfirmDeleteMessage?: (node: any) => string | undefined;
  /** the node will not be drag-and-droppable */
  isNotDraggable?: true;
  /** you will not be able to open the aside and will not be able to edit inline */
  readOnlyNode?: true;
  /** show the NodeTypeConfig.information.single in the document (because there will be no text for this node, it is a container) */
  showPlaceholder?: true;
  /**
   * when a new building block node is dropped from the ribbon, there are three options:
   * - the carrot needs to be focused directly in the InlineEditor of this new node in the Document (focusOnfield: {the field in which the carrot needs to focus})
   * - the aside needs to be opened for this new node (openAside: true)
   * - nothing needs to be done (openAside: false)
   */
  onNewNodeDropped: RequireExactlyOne<{
    focusOnField: InlineField; // focus the editor to this field when a new node is dropped
    openAside: boolean; // open the aside when a new node is dropped
  }> & {
    dispatchAction?: ActionCreatorWithPayload<{ newHref: ContentHref }>;
  };
  themesDisplaySuffix?: string;
  nodeConditional?: Array<{
    field: string;
    value: any;
    condition: {
      parent?: Array<WebConfigType>;
    };
  }>;
};

/**
 * Configuration for building blocks that are some more specific instance of some other generic building block
 * Given the webconfiguration and/or the type of document in which they are in they become a more specific instance of the building block
 * For example especially SECTION can be very differenct in each kind of document with other meta properties and so they need their own configuration file
 * This is used for instance with the Building Blocks component where only generic Building Blocks are listed
 * so for dragging and dropping it is important: the parent lists the allowed building blocks but that also means you can drop here a more specific instance
 */
export type ExtendedBuildingBlockNodeConfig = BuildingBlockNodeConfig & {
  /** means this is a more spicific instance of the given generic NodeType */
  extends: NodeType; // extend another NodeTypeConfig
  // /** 2nd parameter for NodeType match */
  // webTemplateHref?: WebTemplateHref;
  // /** 3rd parameter for NodeType match: if the root node is of this NodeType */
  // documentTypes?: Array<NodeType>;
};

export type NodeTypeConfig =
  | RootNodeConfig
  | ExtendedRootNodeConfig
  | BuildingBlockNodeConfig
  | ExtendedBuildingBlockNodeConfig
  | HiddenContentItemConfig
  | (RootNodeConfig & BuildingBlockNodeConfig); // Teaser

export type NodeTypeConfigApplied = Merge<
  NodeTypeConfig,
  {
    edit: Array<FilteredEditComponent>;
    buildingBlocks: Array<FilteredBuildingBlock>;
    audienceTab?: Array<AudienceTabComponent>;
    webTab?: Array<WebTabComponent>;
  }
>;

export const isGenericRootNodeConfig = (
  nodeTypeConfig: NodeTypeConfig,
): nodeTypeConfig is RootNodeConfig =>
  'isSearchable' in nodeTypeConfig && !('extends' in nodeTypeConfig);

export const isRootNodeConfig = (
  nodeTypeConfig: NodeTypeConfig,
): nodeTypeConfig is
  | RootNodeConfig
  | ExtendedRootNodeConfig
  | (RootNodeConfig & BuildingBlockNodeConfig) => 'tocTypes' in nodeTypeConfig;

export const isExtendedRootNodeConfig = (
  nodeTypeConfig: NodeTypeConfig,
): nodeTypeConfig is ExtendedRootNodeConfig => 'extends' in nodeTypeConfig;

export const isBuildingBlockNodeConfig = (
  nodeTypeConfig: NodeTypeConfig,
): nodeTypeConfig is
  | BuildingBlockNodeConfig
  | ExtendedBuildingBlockNodeConfig
  | (RootNodeConfig & BuildingBlockNodeConfig) =>
  'isCollapsible' in nodeTypeConfig && 'onNewNodeDropped' in nodeTypeConfig;

export const isHiddenNodeConfig = (
  nodeTypeConfig: NodeTypeConfig,
): nodeTypeConfig is HiddenContentItemConfig => 'hideInDocument' in nodeTypeConfig;

export type NodeTypeSelectors = Record<
  NodeType,
  {
    nodeSelector?: (state: RootState, href: ContentHref) => ContentNode;
    deleteFromListValidations?: Array<(key: string) => Promise<any>>;
    deleteValidations?: Array<(node: any, state?: any) => any>;
    createValidations?: Array<{ cmd: any; failAction: any }>;
    validationRules?: Array<ValidationRule>;
  }
>;

export type DropExistingNode = {
  items: Array<{
    node: { type: NodeType; href: ContentHref };
    relation: IsPartOfRelation | IsIncludedInRelation;
    /**
     * The type to compare the buildingblocks with to check if they are allowed to be dropped
     */
    buildingBlockType: NodeType;
  }>;
};

export type DropBuildingBlock = {
  items: Array<{
    node: { type: NodeType };
    newNode: true;
    /**
     * The type to compare the buildingblocks with to check if they are allowed to be dropped
     */
    buildingBlockType: NodeType;
  }>;
};

export type DropFile = {
  items: DataTransferItemList;
  files: FileList;
};

export type DropType = DropExistingNode | DropBuildingBlock | DropFile;

export const isDropFile = (dropType: DropType): dropType is DropFile => {
  return 'items' in dropType && dropType.items instanceof DataTransferItemList;
};

export type DropFileBlock = {
  items: Array<{
    node: { type: NodeType };
    newNode: true;
    file: File;
  }>;
};

export enum HoverPositionEnum {
  ABOVE = 'ABOVE',
  IN = 'IN',
  BELOW = 'BELOW',
}

export type HoverPosition = HoverPositionEnum | null;
